import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, Heart, Tag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useStore, money } from "@/context/StoreContext";
import { Progress } from "@/components/ui/progress";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

export default function Cart() {
  const { items, removeItem, updateQty, subtotal } = useCart();
  const { toggleWishlist } = useAuth();
  const { settings } = useStore();
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [coupon, setCoupon] = useState(null);

  const threshold = settings?.free_shipping_threshold || 999;
  const discount = coupon?.discount || 0;
  const remaining = Math.max(threshold - subtotal, 0);
  const freeShip = coupon?.free_shipping || subtotal - discount >= threshold;
  const shipping = freeShip ? 0 : (settings?.standard_shipping || 79);
  const tax = Math.round((subtotal - discount) * (settings?.gst_percent || 5) / 100);
  const total = subtotal - discount + shipping + tax;

  const apply = async () => {
    try { const r = await api.post("/coupons/validate", { code, subtotal }); setCoupon(r.data); toast(`Coupon ${r.data.code} applied ♡`); }
    catch (e) { setCoupon(null); toast.error(apiErr(e)); }
  };

  if (items.length === 0) return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center">
      <h1 className="font-serif text-4xl">Your bag is empty</h1>
      <p className="text-sharoa-muted mt-3">Discover pieces made for your everyday ♡</p>
      <Link to="/shop" className="inline-block mt-7 bg-sharoa-rose text-white text-xs uppercase tracking-widest px-8 py-4">Shop Now</Link>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <h1 className="font-serif text-3xl sm:text-4xl mb-8">Shopping Bag</h1>
      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 divide-y divide-sharoa-line border-y border-sharoa-line">
          {items.map((it) => (
            <div key={it.sku} className="flex gap-4 py-5">
              <img src={it.image} alt={it.name} className="w-24 h-32 object-cover bg-sharoa-ivory" />
              <div className="flex-1">
                <div className="flex justify-between">
                  <Link to={`/product/${it.slug}`} className="font-serif text-lg hover:text-sharoa-rose">{it.name}</Link>
                  <button onClick={() => removeItem(it.sku)} data-testid={`cart-remove-${it.sku}`}><Trash2 size={16} className="text-sharoa-muted hover:text-sharoa-rose" /></button>
                </div>
                <p className="text-sm text-sharoa-muted mt-1">Size {it.size} · {it.color}</p>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center border border-sharoa-line">
                    <button onClick={() => updateQty(it.sku, it.quantity - 1)} className="px-2.5 py-1.5"><Minus size={14} /></button>
                    <span className="px-3 font-mono text-sm">{it.quantity}</span>
                    <button onClick={() => updateQty(it.sku, it.quantity + 1)} className="px-2.5 py-1.5"><Plus size={14} /></button>
                  </div>
                  <span className="font-mono">{money(it.price * it.quantity)}</span>
                </div>
                <button onClick={() => { toggleWishlist(it.product_id); removeItem(it.sku); }} className="text-xs text-sharoa-muted hover:text-sharoa-rose mt-3 flex items-center gap-1"><Heart size={12} /> Move to Wishlist</button>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-1">
          <div className="bg-sharoa-ivory p-6 sticky top-24">
            {remaining > 0 ? <p className="text-xs mb-2">You are <span className="font-mono text-sharoa-rose">{money(remaining)}</span> away from FREE SHIPPING ♡</p>
              : <p className="text-xs text-sharoa-rose mb-2 font-medium">You've unlocked FREE SHIPPING ♡</p>}
            <Progress value={Math.min(subtotal / threshold * 100, 100)} className="h-1.5 mb-5" />
            <div className="flex gap-2 mb-5">
              <input data-testid="coupon-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="ENTER PROMO CODE"
                className="flex-1 border border-sharoa-line bg-white px-3 py-2.5 text-xs uppercase tracking-widest outline-none focus:border-sharoa-rose" />
              <button data-testid="coupon-apply-btn" onClick={apply} className="bg-sharoa-charcoal text-white text-xs uppercase tracking-widest px-4">Apply</button>
            </div>
            {coupon && <p className="text-xs text-sharoa-success flex items-center gap-1 mb-3"><Tag size={12} /> {coupon.code} applied</p>}
            <div className="space-y-2.5 text-sm border-t border-sharoa-line pt-4">
              <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{money(subtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between text-sharoa-rose"><span>Discount</span><span className="font-mono">−{money(discount)}</span></div>}
              <div className="flex justify-between"><span>Shipping</span><span className="font-mono">{shipping === 0 ? "FREE" : money(shipping)}</span></div>
              <div className="flex justify-between"><span>Tax (GST)</span><span className="font-mono">{money(tax)}</span></div>
              <div className="flex justify-between font-serif text-lg border-t border-sharoa-line pt-3"><span>Total</span><span className="font-mono">{money(total)}</span></div>
            </div>
            <button data-testid="proceed-checkout-btn" onClick={() => nav("/checkout", { state: { coupon: coupon?.code } })} className="w-full mt-5 bg-sharoa-rose hover:bg-sharoa-rosedark text-white text-xs uppercase tracking-[0.18em] py-4 transition-colors">Proceed to Checkout</button>
            <Link to="/shop" className="block text-center text-xs uppercase tracking-widest mt-3 sh-underline mx-auto w-fit">Continue Shopping</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
